import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  FormLabel,
} from '@mui/material'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getClassSchedulesDirect, createScheduleOverrideDirect, updateScheduleOverrideDirect } from '@timetable/api'
import type {
  ScheduleOverride,
  OverrideType,
  SaturdayPosition,
  DayOfWeekOrAll,
  CreateScheduleOverrideRequest,
} from '@timetable/types'
import { queryKeys } from '../../lib/queryKeys'

// ── Constants ─────────────────────────────────────────────────────────────────

const OVERRIDE_TYPE_LABELS: Record<OverrideType, string> = {
  holiday: 'Holiday',
  half_day_schedule: 'Half Day Schedule',
  alternate_saturday: 'Alternate Saturday',
}

const DAY_OF_WEEK_OPTIONS: { value: DayOfWeekOrAll; label: string }[] = [
  { value: 'ALL', label: 'All Days' },
  { value: 'MONDAY', label: 'Monday' },
  { value: 'TUESDAY', label: 'Tuesday' },
  { value: 'WEDNESDAY', label: 'Wednesday' },
  { value: 'THURSDAY', label: 'Thursday' },
  { value: 'FRIDAY', label: 'Friday' },
  { value: 'SATURDAY', label: 'Saturday' },
  { value: 'SUNDAY', label: 'Sunday' },
]

const WEEK_PARITY_OPTIONS: { value: SaturdayPosition; label: string }[] = [
  { value: 1, label: '1st Saturday' },
  { value: 2, label: '2nd Saturday' },
  { value: 3, label: '3rd Saturday' },
  { value: 4, label: '4th Saturday' },
  { value: 5, label: '5th Saturday' },
]

// ── Form state ────────────────────────────────────────────────────────────────

interface FormState {
  name: string
  override_type: OverrideType
  start_date: string
  end_date: string
  day_of_week: DayOfWeekOrAll | ''
  alternate_schedule_id: string
  working_saturday_positions: SaturdayPosition[]
}

function defaultFormState(override?: ScheduleOverride | null): FormState {
  if (override) {
    return {
      name: override.name,
      override_type: override.override_type,
      start_date: override.start_date,
      end_date: override.end_date,
      day_of_week: override.day_of_week ?? '',
      alternate_schedule_id: override.alternate_schedule_id ?? '',
      working_saturday_positions: override.working_saturday_positions ?? [],
    }
  }
  return {
    name: '',
    override_type: 'holiday',
    start_date: '',
    end_date: '',
    day_of_week: '',
    alternate_schedule_id: '',
    working_saturday_positions: [],
  }
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface ScheduleOverrideFormDialogProps {
  open: boolean
  onClose: () => void
  schoolId: string
  override?: ScheduleOverride | null
  onSuccess: (override: ScheduleOverride) => void
}

// ── Component ─────────────────────────────────────────────────────────────────

export const ScheduleOverrideFormDialog: React.FC<ScheduleOverrideFormDialogProps> = ({
  open,
  onClose,
  schoolId,
  override,
  onSuccess,
}) => {
  const isEditMode = !!override
  const queryClient = useQueryClient()

  const [form, setForm] = React.useState<FormState>(() => defaultFormState(override))
  const [fieldErrors, setFieldErrors] = React.useState<Partial<Record<keyof FormState, string>>>({})
  const [serverError, setServerError] = React.useState<string | null>(null)
  const [warning, setWarning] = React.useState<string | null>(null)

  // Reset form when dialog opens/override changes
  React.useEffect(() => {
    if (open) {
      setForm(defaultFormState(override))
      setFieldErrors({})
      setServerError(null)
      setWarning(null)
    }
  }, [open, override])

  // Fetch class schedules for alternate schedule selector
  const { data: classSchedules = [], isLoading: loadingSchedules } = useQuery({
    queryKey: queryKeys.classSchedules.list({ school_id: schoolId }),
    queryFn: () => getClassSchedulesDirect(schoolId),
    enabled: open && !!schoolId,
  })

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm(prev => ({ ...prev, [key]: value }))
    setFieldErrors(prev => ({ ...prev, [key]: undefined }))
    setServerError(null)
  }

  // When override_type changes, clear type-specific fields
  const handleTypeChange = (newType: OverrideType) => {
    setForm(prev => ({
      ...prev,
      override_type: newType,
      day_of_week: '',
      alternate_schedule_id: '',
      working_saturday_positions: [],
    }))
    setFieldErrors({})
    setServerError(null)
  }

  // ── Validation ──────────────────────────────────────────────────────────────

  const validate = (): boolean => {
    const errors: Partial<Record<keyof FormState, string>> = {}

    if (!form.name.trim()) {
      errors.name = 'Name is required'
    }

    if (!form.start_date) {
      errors.start_date = 'Start date is required'
    }

    if (!form.end_date) {
      errors.end_date = 'End date is required'
    }

    // Req 1.4: end_date >= start_date
    if (form.start_date && form.end_date && form.end_date < form.start_date) {
      errors.end_date = 'End date must be on or after start date'
    }

    if (form.override_type === 'half_day_schedule') {
      // Req 2.2: requires day_of_week (including ALL per Req 4.1)
      if (!form.day_of_week) {
        errors.day_of_week = 'Target day is required'
      }
      // Req 2.2: requires alternate schedule
      if (!form.alternate_schedule_id) {
        errors.alternate_schedule_id = 'Alternate schedule is required'
      }
      // Req 2.4: alternate_schedule_id must belong to same school (enforced by only showing school schedules)
    }

    if (form.override_type === 'alternate_saturday') {
      // Req 3.1: requires working_saturday_positions
      if (!form.working_saturday_positions || form.working_saturday_positions.length === 0) {
        errors.working_saturday_positions = 'At least one Saturday position must be selected'
      }
      // Req 3.1: requires alternate schedule
      if (!form.alternate_schedule_id) {
        errors.alternate_schedule_id = 'Alternate schedule is required'
      }
    }

    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  // ── Mutations ───────────────────────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: (req: CreateScheduleOverrideRequest) => createScheduleOverrideDirect(req),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.scheduleOverrides.all })
      if (res.warning) setWarning(res.warning)
      onSuccess(res.override)
      if (!res.warning) onClose()
    },
    onError: (err: Error) => {
      setServerError(err.message)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, req }: { id: string; req: Parameters<typeof updateScheduleOverrideDirect>[1] }) =>
      updateScheduleOverrideDirect(id, req),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.scheduleOverrides.all })
      if (res.warning) setWarning(res.warning)
      onSuccess(res.override)
      if (!res.warning) onClose()
    },
    onError: (err: Error) => {
      setServerError(err.message)
    },
  })

  const isPending = createMutation.isPending || updateMutation.isPending

  // ── Submit ──────────────────────────────────────────────────────────────────

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return

    const payload: CreateScheduleOverrideRequest = {
      school_id: schoolId,
      name: form.name.trim(),
      override_type: form.override_type,
      start_date: form.start_date,
      end_date: form.end_date,
    }

    if (form.override_type === 'half_day_schedule') {
      payload.day_of_week = form.day_of_week as DayOfWeekOrAll
      payload.alternate_schedule_id = form.alternate_schedule_id
    }

    if (form.override_type === 'alternate_saturday') {
      payload.working_saturday_positions = form.working_saturday_positions
      payload.alternate_schedule_id = form.alternate_schedule_id
    }

    if (isEditMode && override) {
      const { school_id, ...updatePayload } = payload
      updateMutation.mutate({ id: override.id, req: updatePayload })
    } else {
      createMutation.mutate(payload)
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onClose={isPending ? undefined : onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {isEditMode ? 'Edit Schedule Override' : 'Add Schedule Override'}
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {serverError && (
              <Alert severity="error">{serverError}</Alert>
            )}
            {warning && (
              <Alert severity="warning" onClose={() => setWarning(null)}>
                {warning}
              </Alert>
            )}

            {/* Override Type */}
            <FormControl fullWidth required>
              <InputLabel id="override-type-label">Override Type</InputLabel>
              <Select
                labelId="override-type-label"
                value={form.override_type}
                onChange={(e) => handleTypeChange(e.target.value as OverrideType)}
                label="Override Type"
                disabled={isEditMode}
              >
                {(Object.keys(OVERRIDE_TYPE_LABELS) as OverrideType[]).map((type) => (
                  <MenuItem key={type} value={type}>
                    {OVERRIDE_TYPE_LABELS[type]}
                  </MenuItem>
                ))}
              </Select>
              {isEditMode && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
                  Override type cannot be changed after creation
                </Typography>
              )}
            </FormControl>

            {/* Name */}
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              required
              fullWidth
              autoFocus={!isEditMode}
              placeholder={
                form.override_type === 'holiday'
                  ? 'e.g., Diwali Holiday'
                  : form.override_type === 'half_day_schedule'
                  ? 'e.g., Friday Half Day'
                  : 'e.g., Alternate Saturday (Odd)'
              }
              error={!!fieldErrors.name}
              helperText={fieldErrors.name}
            />

            {/* half_day_schedule: Target Day of Week (Req 2.2, 4.1) */}
            {form.override_type === 'half_day_schedule' && (
              <FormControl fullWidth required error={!!fieldErrors.day_of_week}>
                <InputLabel id="day-of-week-label">Target Day of Week</InputLabel>
                <Select
                  labelId="day-of-week-label"
                  value={form.day_of_week}
                  onChange={(e) => setField('day_of_week', e.target.value as DayOfWeekOrAll)}
                  label="Target Day of Week"
                >
                  {DAY_OF_WEEK_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                {fieldErrors.day_of_week && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {fieldErrors.day_of_week}
                  </Typography>
                )}
              </FormControl>
            )}

            {/* alternate_saturday: Working Saturday Positions checkboxes (Req 3.1) */}
            {form.override_type === 'alternate_saturday' && (
              <FormControl
                component="fieldset"
                required
                error={!!fieldErrors.working_saturday_positions}
              >
                <FormLabel component="legend" sx={{ mb: 0.5 }}>
                  Working Saturdays
                </FormLabel>
                <FormGroup row>
                  {WEEK_PARITY_OPTIONS.map((opt) => (
                    <FormControlLabel
                      key={opt.value}
                      label={opt.label}
                      control={
                        <Checkbox
                          checked={form.working_saturday_positions.includes(opt.value)}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? ([...form.working_saturday_positions, opt.value].sort((a, b) => a - b) as SaturdayPosition[])
                              : (form.working_saturday_positions.filter((p) => p !== opt.value) as SaturdayPosition[])
                            setField('working_saturday_positions', next)
                          }}
                          inputProps={{ 'aria-label': opt.label }}
                        />
                      }
                    />
                  ))}
                </FormGroup>
                {fieldErrors.working_saturday_positions && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    {fieldErrors.working_saturday_positions}
                  </Typography>
                )}
              </FormControl>
            )}

            {/* Alternate Schedule selector (half_day_schedule + alternate_saturday) (Req 2.2, 2.4, 3.1) */}
            {(form.override_type === 'half_day_schedule' || form.override_type === 'alternate_saturday') && (
              <FormControl fullWidth required error={!!fieldErrors.alternate_schedule_id}>
                <InputLabel id="alt-schedule-label">Alternate Schedule</InputLabel>
                <Select
                  labelId="alt-schedule-label"
                  value={form.alternate_schedule_id}
                  onChange={(e) => setField('alternate_schedule_id', e.target.value)}
                  label="Alternate Schedule"
                  disabled={loadingSchedules}
                  startAdornment={loadingSchedules ? <CircularProgress size={16} sx={{ mr: 1 }} /> : undefined}
                >
                  {classSchedules.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                      {s.description ? ` — ${s.description}` : ''}
                    </MenuItem>
                  ))}
                </Select>
                {fieldErrors.alternate_schedule_id && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {fieldErrors.alternate_schedule_id}
                  </Typography>
                )}
                {classSchedules.length === 0 && !loadingSchedules && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
                    No class schedules found for this school
                  </Typography>
                )}
              </FormControl>
            )}

            {/* Start Date */}
            <TextField
              label="Start Date"
              type="date"
              value={form.start_date}
              onChange={(e) => setField('start_date', e.target.value)}
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
              error={!!fieldErrors.start_date}
              helperText={fieldErrors.start_date}
            />

            {/* End Date */}
            <TextField
              label={form.override_type === 'holiday' ? 'End Date (optional, defaults to start date)' : 'End Date'}
              type="date"
              value={form.end_date}
              onChange={(e) => setField('end_date', e.target.value)}
              required={form.override_type !== 'holiday'}
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: form.start_date || undefined }}
              error={!!fieldErrors.end_date}
              helperText={fieldErrors.end_date}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={isPending}>
            {isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : isEditMode ? (
              'Update'
            ) : (
              'Create'
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
